import { promScoreMessages } from "../../helpers/messages";
import { scoreTypesForManualScore } from "./constants";

export const customValidationScoreTypes = ['Oxford Knee Score', 'EQ5D'];

/**
 * @description Function for validate each input field
 * @param field Field name
 * @param value Field value
 * @param errors Errors state object
 * @returns Object with error messages
 * 
 */
export const validatePromFields = (field, value, errors) => {
    var errorFlag = false;

    switch (field) {
        case 'scoreType':
            if (value === '') {
                errors.scoreType = promScoreMessages.error.scoreType;
                errorFlag = true;
            } else {
                errors.scoreType = '';
            }
            break;
        case 'subScoreType':
            if (value === '') {
                errors.subScoreType = promScoreMessages.error.subScoreType;
                errorFlag = true;
            } else {
                errors.subScoreType = '';
            }
            break;
        case 'timeFrame':
            if (value === '') {
                errors.timeFrame = promScoreMessages.error.timeFrame;
                errorFlag = true;
            } else {
                errors.timeFrame = '';
            }
            break;

        default:
            break;
    }

    return { errorFlag, errors }
}

/**
 * @description Function for validate prom score
 * @param scoreType PROM score type
 * @param value PROM score value
 * @param errors Errors state object
 * @returns Object with error messages
 * 
 */
export const validatePromScore = (scoreType, value, errors, formSubmit = false, manualScoresObj = null) => {
    if (scoreTypesForManualScore.indexOf(scoreType) > -1 && manualScoresObj) {
        return validateManualScores(scoreType, manualScoresObj, errors, formSubmit);
    }
    var errorFlag = false;
    var scoreLimit = 100;
    const decimalRegexOnePlace = new RegExp(/^\d+\.?\d{0,1}$/);
    const decimalRegexTwoPlaces = new RegExp(/^\d+\.?\d{0,2}$/);
    const decimalRegexThreePlaces = new RegExp(/^\d+\.?\d{0,3}$/);
    if (value === '') {
        errors.scoreValue = promScoreMessages.error.scoreValue;
        errorFlag = true;
    } else if (!scoreType) {
        errors.scoreValue = promScoreMessages.error.noScoreTypeSelected;
        errorFlag = true;
    } else if ((parseFloat(value) < 0) || (1 / Math.sign(value)) === -Infinity) {
        errors.scoreValue = promScoreMessages.error.negativeValue;
        errorFlag = true;
    } else if (['KOOS JR.', 'HOOS JR.', 'PROMIS-Global', 'VAS Pain'].indexOf(scoreType) === -1 && (value.toString().indexOf(".") !== -1 || !Number.isInteger(parseFloat(value)))) {
        errors.scoreValue = promScoreMessages.error.decimalValue;
        errorFlag = true;
    } else {
        switch (scoreType) {
            case 'Oxford Knee Score':
                scoreLimit = 48;
                break;
            case 'PROMIS-Global':
                scoreLimit = 20;
                if (!decimalRegexTwoPlaces.test(value)) {
                    errorFlag = true;
                    errors.scoreValue = promScoreMessages.error.decimalValuePlaces(2);
                    return { errorFlag, errors };
                }
                break;
            case 'KOOS JR.':
            case 'HOOS JR.':
                if (!decimalRegexThreePlaces.test(value)) {
                    errorFlag = true;
                    errors.scoreValue = promScoreMessages.error.decimalValuePlaces(3);
                    return { errorFlag, errors };
                }
                break;
            case 'VAS Pain':
                scoreLimit = 10;
                if (!decimalRegexOnePlace.test(value)) {
                    errorFlag = true;
                    errors.scoreValue = promScoreMessages.error.decimalValuePlaces(1);
                    return { errorFlag, errors };
                }
                break;
            case 'SST':
                scoreLimit = 12;
                break;
            default:
                break;
        }
        if (parseFloat(value) > scoreLimit) {
            errors.scoreValue = promScoreMessages.error.scoreLimit(scoreLimit);
            errorFlag = true;
        } else {
            errors.scoreValue = '';
        }
    }

    return { errorFlag, errors };
}

/**
 * @description Function for validate sub scores
 * @param scoreType PROM score type
 * @param manualScoresObj PROM sub score object
 * @param errors Errors state object
 * @param formSubmit Flag for form submission
 * @returns Object with error messages
 * 
 */
export const validateManualScores = (scoreType, manualScoresObj, errors, formSubmit) => {
    var fields = Object.keys(manualScoresObj);
    var errorFlag = false;
    var hasAtLeastOneValue = false;

    if (fields.length) {
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            const value = manualScoresObj[field];
            if (value !== '') {
                errors.manualScoreOneRequired = '';
                hasAtLeastOneValue = true;
            }

            switch (scoreType) {
                case 'KOOS':
                case 'HOOS':
                    let validateKOOSObj = validateKOOS(field, value, errors);
                    if (validateKOOSObj.errorFlag) {
                        return validateKOOSObj;
                    }
                    break;
                case 'EQ5D':
                    let validateEQ5DObj = validateEQ5D(field, value, errors);
                    if (validateEQ5DObj.errorFlag) {
                        return validateEQ5DObj;
                    }
                    break;
                case 'ASES':
                    let validateASESObj = validateASES(field, value, errors);
                    if (validateASESObj.errorFlag) {
                        return validateASESObj;
                    }
                    break;
                case 'CS':
                    let validateCSObj = validateCS(field, value, errors);
                    if (validateCSObj.errorFlag) {
                        return validateCSObj;
                    }
                    break;
                default:
                    break;
            }
        }
    }

    if (formSubmit && !hasAtLeastOneValue) {
        errors.manualScoreOneRequired = promScoreMessages.error.manualScoreOneRequired;
        errorFlag = true;
    }

    return { errorFlag, errors };
}

/**
 * @description Function for validate KOOS/HOOS subscores
 * @param field KOOS/HOOS subscore type
 * @param value Score value
 * @param errors Errors state object
 * @returns Object with error messages
 * 
 */
export const validateKOOS = (field, value, errors) => {
    var errorFlag = false;
    if (!value) {
        errors[field] = '';
    } else {
        var scoreLimit = 100;
        var decimalRegex = new RegExp(/^\d+\.?\d{0,2}$/);
        errors.manualScoreOneRequired = '';

        if ((parseFloat(value) < 0) || (1 / Math.sign(value)) === -Infinity) {
            errors[field] = promScoreMessages.error.negativeValue;
            errorFlag = true;
        } else if (!decimalRegex.test(value)) {
            errors[field] = promScoreMessages.error.decimalValuePlaces(2);
            errorFlag = true;
        } else if (parseFloat(value) > scoreLimit) {
            errors[field] = promScoreMessages.error.scoreLimit(scoreLimit);
            errorFlag = true;
        } else {
            errors[field] = '';
        }
    }

    return { errorFlag, errors };
}

/**
 * @description Function for validate EQ5D subscores
 * @param field EQ5D subscore type
 * @param value Score value
 * @param errors Errors state object
 * @returns Object with error messages
 * 
 */
export const validateEQ5D = (field, value, errors) => {
    var errorFlag = false;
    if (!value) {
        errors[field] = '';
    } else {
        var Regex = new RegExp(/^[1-5]{1}$/);
        if ((parseInt(value) < 0) || (1 / Math.sign(value)) === -Infinity) {
            errors[field] = promScoreMessages.error.negativeValue;
            errorFlag = true;
        } else if (value.toString().indexOf(".") !== -1 || !Number.isInteger(parseFloat(value))) {
            errors[field] = promScoreMessages.error.decimalValue;
            errorFlag = true;
        } else if (!Regex.test(value)) {
            errors[field] = promScoreMessages.error.EQ5DRegex;
            errorFlag = true;
        } else {
            errors[field] = '';
        }
    }

    return { errorFlag, errors };
}

/**
 * @description Function for validate ASES subscores
 * @param field ASES subscore type
 * @param value Score value
 * @param errors Errors state object
 * @returns Object with error messages
 * 
 */
export const validateASES = (field, value, errors) => {
    var errorFlag = false;
    if (!value) {
        errors[field] = '';
    } else {
        var scoreLimit;
        var decimalRegex = new RegExp(/^\d+\.?\d{0,1}$/);
        errors.manualScoreOneRequired = '';
        switch (field) {
            case 'ASESPain':
            case 'ASESADL':
                scoreLimit = 50;
                break;
            default:
                break;
        }

        if ((parseFloat(value) < 0) || (1 / Math.sign(value)) === -Infinity) {
            errors[field] = promScoreMessages.error.negativeValue;
            errorFlag = true;
        } else if (!decimalRegex.test(value)) {
            errors[field] = promScoreMessages.error.decimalValuePlaces(1);
            errorFlag = true;
        } else if (scoreLimit && parseFloat(value) > scoreLimit) {
            errors[field] = promScoreMessages.error.scoreLimit(scoreLimit);
            errorFlag = true;
        } else {
            errors[field] = '';
        }
    }

    return { errorFlag, errors };
}

/**
 * @description Function for validate CS subscores
 * @param field ASES subscore type
 * @param value Score value
 * @param errors Errors state object
 * @returns Object with error messages
 * 
 */
export const validateCS = (field, value, errors) => {
    var errorFlag = false;
    if (!value) {
        errors[field] = '';
    } else {
        var decimalRegex = new RegExp(/^\d+\.?\d{0,1}$/);
        var scoreLimit;
        errors.manualScoreOneRequired = '';
        switch (field) {
            case 'CSPain':
                scoreLimit = 15;
                break;
            case 'CSADL':
                scoreLimit = 20;
                break;
            case 'CSROM':
                scoreLimit = 40;
                break;
            case 'CSStrength':
                scoreLimit = 25;
                break;

            default:
                break;
        }
        if ((parseFloat(value) < 0) || (1 / Math.sign(value)) === -Infinity) {
            errors[field] = promScoreMessages.error.negativeValue;
            errorFlag = true;
        } else if (!decimalRegex.test(value)) {
            errors[field] = promScoreMessages.error.decimalValuePlaces(1);
            errorFlag = true;
        } else if (scoreLimit && parseFloat(value) > scoreLimit) {
            errors[field] = promScoreMessages.error.scoreLimit(scoreLimit);
            errorFlag = true;
        } else {
            errors[field] = '';
        }
    }

    return { errorFlag, errors };
}